import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Upload,
  Wand2,
  Download,
  Loader2,
  X,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// Serbest prompt'un sonuna her durumda eklenen çekirdek koruma kuralları.
// Çıktının orijinal görselle aynı oran/boyutta kalmasını ve AI üretim izinin
// olmamasını garanti eder.
const GUARD_RULES = `

KORUNACAK KURALLAR (kesinlikle uy):
- Çıktının en-boy oranı girdi görselle birebir aynı olsun.
- Sadece istenen değişikliği yap; istenmeyen hiçbir şeyi değiştirme, ekleme veya silme.
- Ürün/öznenin formu, rengi, oranları ve marka detayları (aksi açıkça istenmedikçe) korunsun.
- Gerçekçi fotoğraf izlenimi ver — yapay "AI üretimi" görünümü, deformasyon veya artefakt yok.
- Yazı/logo varsa ve değiştirilmesi istenmediyse okunaklı ve bozulmamış kalsın.`;

async function loadImageMeta(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Görsel okunamadı'));
      i.src = url;
    });
    return {
      img,
      naturalWidth: img.naturalWidth || img.width,
      naturalHeight: img.naturalHeight || img.height,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function buildPayloadAndPreview(file, maxSide = 1600, quality = 0.92) {
  const { img, naturalWidth, naturalHeight } = await loadImageMeta(file);
  const scale = Math.min(1, maxSide / Math.max(naturalWidth, naturalHeight));
  const cw = Math.max(1, Math.round(naturalWidth * scale));
  const ch = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, cw, ch);
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  const base64 = dataUrl.split(',')[1] || '';
  return {
    payload: { mimeType: 'image/jpeg', data: base64 },
    preview: dataUrl,
    originalWidth: naturalWidth,
    originalHeight: naturalHeight,
    sentWidth: cw,
    sentHeight: ch,
  };
}

function aspectRatioFor(w, h) {
  const r = w / h;
  const candidates = [
    { v: '1:1', r: 1 },
    { v: '3:4', r: 3 / 4 },
    { v: '4:3', r: 4 / 3 },
    { v: '9:16', r: 9 / 16 },
    { v: '16:9', r: 16 / 9 },
  ];
  let best = candidates[0];
  let bestDiff = Math.abs(Math.log(r / best.r));
  for (const c of candidates) {
    const d = Math.abs(Math.log(r / c.r));
    if (d < bestDiff) { bestDiff = d; best = c; }
  }
  return best.v;
}

function sampleBackgroundColor(img) {
  const tmp = document.createElement('canvas');
  tmp.width = img.width;
  tmp.height = img.height;
  const tctx = tmp.getContext('2d');
  tctx.drawImage(img, 0, 0);

  const cornerSize = Math.max(8, Math.round(Math.min(img.width, img.height) * 0.03));
  const cw = Math.min(cornerSize, img.width);
  const ch = Math.min(cornerSize, img.height);
  const corners = [
    [0, 0],
    [img.width - cw, 0],
    [0, img.height - ch],
    [img.width - cw, img.height - ch],
  ];

  const rs = [], gs = [], bs = [];
  for (const [x, y] of corners) {
    try {
      const data = tctx.getImageData(x, y, cw, ch).data;
      let sr = 0, sg = 0, sb = 0, n = 0;
      for (let i = 0; i < data.length; i += 4) {
        sr += data[i]; sg += data[i + 1]; sb += data[i + 2]; n++;
      }
      if (n) { rs.push(sr / n); gs.push(sg / n); bs.push(sb / n); }
    } catch (_) { /* tainted canvas — skip */ }
  }
  if (!rs.length) return '#FFFFFF';
  const median = (arr) => {
    const s = [...arr].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };
  const r = Math.round(median(rs));
  const g = Math.round(median(gs));
  const b = Math.round(median(bs));
  return `rgb(${r}, ${g}, ${b})`;
}

// Modelin döndürdüğü görseli, girdi görselin ORİJİNAL piksel boyutuna oturtur
// (oran farkı varsa köşe rengiyle letterbox). Böylece çıktı her zaman aynı boyutta.
async function fitToOriginalSize(resultBase64, resultMime, targetW, targetH) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Sonuç görseli açılamadı'));
    i.src = `data:${resultMime || 'image/png'};base64,${resultBase64}`;
  });
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = sampleBackgroundColor(img);
  ctx.fillRect(0, 0, targetW, targetH);
  const srcA = img.width / img.height;
  const dstA = targetW / targetH;
  let dw, dh, dx, dy;
  if (Math.abs(srcA - dstA) < 0.01) {
    dw = targetW; dh = targetH; dx = 0; dy = 0;
  } else if (srcA > dstA) {
    dw = targetW; dh = Math.round(targetW / srcA); dx = 0; dy = Math.round((targetH - dh) / 2);
  } else {
    dh = targetH; dw = Math.round(targetH * srcA); dy = 0; dx = Math.round((targetW - dw) / 2);
  }
  ctx.drawImage(img, dx, dy, dw, dh);
  const outDataUrl = canvas.toDataURL('image/jpeg', 0.95);
  return { dataUrl: outDataUrl, base64: outDataUrl.split(',')[1] || '', mimeType: 'image/jpeg' };
}

const EXAMPLE_PROMPTS = [
  'Arka planı temiz beyaz yap, ürüne dokunma.',
  'Işığı ve renkleri profesyonel stüdyo kalitesinde dengele.',
  'Görseli netleştir, gürültüyü azalt, detayları koru.',
  'Ürünün altına doğal yumuşak bir gölge ekle.',
];

export default function ImageEnhance() {
  const [model, setModel] = useState('gemini-3-pro-image-preview');
  const [source, setSource] = useState(null); // { ...buildPayload, name, size }
  const [result, setResult] = useState(null); // { dataUrl, data, mimeType, message }
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') ||
        ''
      );
    } catch (_) {
      return '';
    }
  }, []);

  const loadFile = useCallback(async (file) => {
    setError('');
    if (!file || !String(file.type || '').startsWith('image/')) {
      setError('Lütfen bir görsel dosyası seç (PNG / JPG / WebP).');
      return;
    }
    try {
      const b = await buildPayloadAndPreview(file, 1600, 0.92);
      setSource({ ...b, name: file.name, size: file.size });
      setResult(null);
    } catch (e) {
      setError(e?.message || 'Görsel işlenemedi.');
    }
  }, []);

  const onFileInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) loadFile(f);
    e.target.value = '';
  };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) loadFile(f);
  };

  const reset = useCallback(() => {
    if (loading) return;
    setSource(null);
    setResult(null);
    setError('');
  }, [loading]);

  const run = useCallback(async () => {
    if (loading) return;
    if (!source) { setError('Önce bir görsel yükle.'); return; }
    if (!prompt.trim()) { setError('İyileştirme isteğini yaz.'); return; }

    setError('');
    setLoading(true);
    setResult(null);
    try {
      const aspectRatio = aspectRatioFor(source.originalWidth, source.originalHeight);
      const promptText = `Görevin: ${prompt.trim()}${GUARD_RULES}`;
      const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          model,
          aspectRatio,
          temperature: 0.4,
          messages: [
            { role: 'user', content: promptText, images: [source.payload] },
          ],
        }),
      });
      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { message: txt }; }
      if (!resp.ok) {
        const detailsStr = (() => {
          const d = json?.details;
          if (!d) return '';
          if (typeof d === 'string') return d.slice(0, 500);
          try { return JSON.stringify(d).slice(0, 500); } catch { return ''; }
        })();
        throw new Error([json?.error, detailsStr].filter(Boolean).join(' — ') || resp.statusText);
      }
      const images = Array.isArray(json?.images) ? json.images : [];
      if (!images.length) {
        const modelText = (json?.message || '').trim();
        throw new Error(modelText
          ? `Model görsel yerine yazı döndü: "${modelText.slice(0, 220)}". Promptu netleştir veya tekrar dene.`
          : 'Model görsel üretmedi. Promptu netleştir veya tekrar dene.');
      }
      const first = images[0];
      const fitted = await fitToOriginalSize(
        first.data,
        first.mimeType || 'image/png',
        source.originalWidth,
        source.originalHeight,
      );
      setResult({
        mimeType: fitted.mimeType,
        data: fitted.base64,
        dataUrl: fitted.dataUrl,
        message: json?.message || '',
      });
    } catch (e) {
      setError(e?.message || 'Görsel üretilemedi.');
    } finally {
      setLoading(false);
    }
  }, [loading, source, prompt, model, brandId]);

  const useResultAsSource = useCallback(() => {
    if (!result || !source) return;
    setSource({
      ...source,
      payload: { mimeType: 'image/jpeg', data: result.data },
      preview: result.dataUrl,
      sentWidth: source.originalWidth,
      sentHeight: source.originalHeight,
      name: `${(source.name || 'image').replace(/\.[a-z0-9]+$/i, '')}_iter.jpg`,
    });
    setResult(null);
  }, [result, source]);

  const downloadResult = useCallback(() => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.dataUrl;
    const baseName = (source?.name || 'image').replace(/\.[a-z0-9]+$/i, '');
    a.download = `${baseName}_enhanced_glowytics.jpg`;
    a.click();
  }, [result, source]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold ring-1 ring-violet-200 mb-3">
            <Sparkles className="w-3.5 h-3.5" />
            Gemini · Görsel İyileştirme
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Görsel İyileştirici</h1>
          <p className="mt-2 text-gray-600 text-sm max-w-2xl leading-relaxed">
            Bir görsel yükle, ne yapmak istediğini kendi cümlelerinle yaz — Gemini iyileştirip çıktıyı versin.
            Çıktı <strong>girdi görselle aynı en-boy oranı ve boyutta</strong> gelir.
          </p>
        </div>

        {/* Upload */}
        {!source ? (
          <div
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className={`bg-white rounded-2xl ring-1 shadow-sm mb-5 transition-all ${isDragging ? 'ring-2 ring-violet-300 bg-violet-50' : 'ring-gray-200'}`}
          >
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-3 py-14 text-gray-400 hover:text-violet-600 transition-colors"
            >
              <Upload className="w-10 h-10" />
              <div className="text-center">
                <p className="text-sm font-medium">Görseli yükle</p>
                <p className="text-xs">veya buraya sürükle bırak (PNG / JPG / WebP)</p>
              </div>
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm mb-5 px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-xs text-gray-700 truncate flex-1" title={source.name}>{source.name}</span>
            <span className="text-[10px] text-gray-400">{source.originalWidth}×{source.originalHeight}px</span>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="text-[11px] font-medium text-violet-600 hover:text-violet-800 disabled:opacity-50"
            >
              Değiştir
            </button>
            <button
              onClick={reset}
              disabled={loading}
              className="text-gray-400 hover:text-red-600 disabled:opacity-30"
              title="Kaldır"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileInputChange} className="hidden" />

        {/* Prompt */}
        <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-5 mb-5">
          <label className="block text-sm font-semibold text-gray-800 mb-2">Ne yapmak istiyorsun?</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Örn: Arka planı temiz beyaz yap, ışığı dengele, ürünü hiç değiştirme."
            rows={4}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-violet-200 resize-none"
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {EXAMPLE_PROMPTS.map((ex) => (
              <button
                key={ex}
                onClick={() => setPrompt(ex)}
                className="text-[11px] text-gray-600 bg-gray-100 hover:bg-violet-100 hover:text-violet-700 rounded-full px-2.5 py-1 transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-3">
            {/* Model toggle */}
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-gray-500">Model:</span>
              <button
                onClick={() => setModel('gemini-3-pro-image-preview')}
                className={`px-2.5 py-1 rounded-md font-medium ${model === 'gemini-3-pro-image-preview' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Pro (yüksek kalite)
              </button>
              <button
                onClick={() => setModel('gemini-2.5-flash-image')}
                className={`px-2.5 py-1 rounded-md font-medium ${model === 'gemini-2.5-flash-image' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Flash (hızlı)
              </button>
            </div>
            <button
              onClick={run}
              disabled={loading || !source || !prompt.trim()}
              className="ml-auto inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white text-sm font-semibold px-5 py-2 rounded-lg shadow-sm"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              İyileştir
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Before / After */}
        {source && (
          <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Source */}
              <div className="border-b md:border-b-0 md:border-r border-gray-100">
                <div className="px-4 py-2 border-b border-gray-100 text-[11px] font-semibold text-gray-500">ÖNCESİ</div>
                <div className="bg-gray-50 min-h-[280px] flex items-center justify-center p-4">
                  <img src={source.preview} alt="kaynak" className="max-w-full max-h-[440px] object-contain" />
                </div>
              </div>

              {/* Result */}
              <div>
                <div className="px-4 py-2 border-b border-gray-100 text-[11px] font-semibold text-gray-500">SONRASI</div>
                <div className="bg-gray-50 min-h-[280px] flex items-center justify-center p-4 relative">
                  {loading && (
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <Loader2 className="w-7 h-7 animate-spin text-violet-500" />
                      <p className="text-xs">İşleniyor…</p>
                      <p className="text-[10px] text-gray-400">15–40 sn</p>
                    </div>
                  )}
                  {!loading && !result && (
                    <div className="text-center text-gray-400 px-4">
                      <Wand2 className="w-7 h-7 mx-auto mb-2" />
                      <p className="text-xs">İsteğini yaz ve "İyileştir"e bas.</p>
                    </div>
                  )}
                  {!loading && result && (
                    <img src={result.dataUrl} alt="sonuç" className="max-w-full max-h-[440px] object-contain" />
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            {result && !loading && (
              <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center gap-3">
                <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Çıktı orijinal boyutta: {source.originalWidth}×{source.originalHeight}px
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={useResultAsSource}
                    disabled={loading}
                    className="text-[11px] font-medium text-violet-600 hover:text-violet-800 disabled:opacity-50 inline-flex items-center gap-1"
                    title="Sonucu kaynak yap, üzerine yeni istek ver"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Bunun üzerine devam et
                  </button>
                  <button
                    onClick={downloadResult}
                    className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg"
                  >
                    <Download className="w-3.5 h-3.5" /> İndir
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
